import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Card,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  FaStar,
  FaEuroSign,
  FaTimes,
  FaFile,
  FaImage,
  FaPaperPlane,
  FaLock,
} from "react-icons/fa";
import { userAPI } from "../../api/apiSystem";
import Pusher from "pusher-js";
import { useTranslation } from "react-i18next";
import OfferMessageCard from "../chat/OfferMessageCard"; // عدّل المسار لو مختلف
import "../chat/ChatBox.css";
import RatingMessageCard from "../rating/RatingMessageCard";

Pusher.logToConsole = process.env.NODE_ENV === "development";
const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || "1a6dafa4504c853b7f61";

const parseOfferCardMessage = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("__OFFER_CARD__")) return null;
  try {
    return JSON.parse(raw.replace("__OFFER_CARD__", ""));
  } catch {
    return null;
  }
};

const parseRatingCardMessage = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("__RATING_CARD__")) return null;
  try {
    return JSON.parse(raw.replace("__RATING_CARD__", ""));
  } catch {
    return null;
  }
};

export default function OrderDetailsModal({
  show,
  handleClose,
  order,
  unreadCount: externalUnreadCount,
  onMessagesRead,
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);
  const [isChatEnabled, setIsChatEnabled] = useState(true);

  const pusherRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const cleanId = (id) => {
    if (!id) return null;
    if (typeof id === "string" && id.includes("No.")) {
      return parseInt(id.replace("No.", "").trim(), 10);
    }
    return parseInt(id, 10);
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    let str = dateString;
    const ddmmyyyy = str.match(/^(\d{2})[.-](\d{2})[.-](\d{4})\s(.*)$/);
    if (ddmmyyyy) {
      str = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T${ddmmyyyy[4]}`;
    } else if (/^\d{4}-\d{2}-\d{2}\s/.test(str)) {
      str = str.replace(" ", "T");
    }
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateString) => {
    const d = parseDate(dateString);
    if (!d) return "N/A";
    return d.toLocaleDateString("de-DE");
  };

  const formatTime = (dateString) => {
    const d = parseDate(dateString);
    if (!d) return "N/A";
    return d.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
      case "inquiry":
        return "warning";
      case "accepted":
      case "assigned":
        return "primary";
      case "rejected":
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  /** لون كارت العرض حسب حالة الـ order */
  const resolveOfferStatus = (offerData) => {
    const status = order?.status;
    if (status === "assigned" || status === "accepted") return "accepted";
    if (status === "rejected" || status === "cancelled") return "rejected";
    return offerData?.status || "pending";
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const markMessagesAsRead = async () => {
    if (!order || hasMarkedAsRead || unreadCount === 0) return;

    try {
      const transportId = order.transport?.id || order.transport_id;
      const orderId = order.id;
      if (!transportId || !orderId) return;

      const response = await userAPI.transportOrderChat.makeRead(
        transportId,
        orderId,
      );

      if (response.success) {
        setUnreadCount(0);
        setHasMarkedAsRead(true);
        setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
        if (onMessagesRead) onMessagesRead(orderId);
      }
    } catch (err) {
      console.error("❌ Error marking messages as read:", err);
    }
  };

  const fetchMessages = async () => {
    if (!order) return;

    try {
      setLoading(true);
      setError(null);
      setHasMarkedAsRead(false);

      const transportId = order.transport?.id || order.transport_id;
      const orderId = order.id;

      if (!transportId || !orderId) {
        setError("Missing transport or order ID");
        return;
      }

      const response = await userAPI.transportOrderChat.getMessages(
        transportId,
        orderId,
      );

      const isEnabled =
        response?.data?.data?.statistics?.is_chat_enabled ??
        response?.data?.statistics?.is_chat_enabled;
      setIsChatEnabled(isEnabled === undefined ? true : Boolean(isEnabled));

      if (response.success) {
        let messagesData = [];
        let statistics = { unread_count: 0 };

        if (Array.isArray(response.data)) {
          messagesData = response.data;
        } else if (response.data && Array.isArray(response.data.messages)) {
          messagesData = response.data.messages;
          statistics = response.data.statistics || statistics;
        } else if (response.data && Array.isArray(response.data.data)) {
          messagesData = response.data.data;
        } else if (
          response.data &&
          Array.isArray(response.data.data?.messages)
        ) {
          messagesData = response.data.data.messages;
          statistics = response.data.data.statistics || statistics;
        }

        setMessages(messagesData);
        setUnreadCount(statistics.unread_count || 0);

        messagesData.forEach((msg) => {
          processedMessageIds.current.add(msg.id);
        });

        if (messagesData.length > 0) {
          const customerMessage = messagesData.find(
            (msg) =>
              msg.sender?.role === "Customer" ||
              msg.sender_type === "customer",
          );
          if (customerMessage) {
            setCurrentUser(customerMessage.sender);
          }
        }

        if (statistics.unread_count > 0) {
          setTimeout(() => {
            markMessagesAsRead();
          }, 500);
        }
      } else {
        setError(response.message || "Failed to fetch messages");
      }
    } catch (err) {
      console.error("❌ Error fetching messages:", err);
      setError(err.message || "An error occurred while fetching messages");
    } finally {
      setLoading(false);
    }
  };

  // Pusher
  useEffect(() => {
    if (!show || !order) return;

    const cleanOrderId = cleanId(order.id);
    if (!cleanOrderId) return;

    if (pusherRef.current) {
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    const pusher = new Pusher(PUSHER_KEY, {
      cluster: "mt1",
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
    });
    pusherRef.current = pusher;

    const channel = pusher.subscribe(`chat.transport_order.${cleanOrderId}`);

    const handleNewMessage = (data) => {
      const hasCompleteAttachments =
        data.attachments &&
        data.attachments.length > 0 &&
        data.attachments[0]?.file_url;

      if (processedMessageIds.current.has(data.id)) {
        if (hasCompleteAttachments) {
          setMessages((prev) =>
            prev.map((msg) => (msg.id === data.id ? { ...msg, ...data } : msg)),
          );
          scrollToBottom();
        }
        return;
      }

      processedMessageIds.current.add(data.id);

      if (data.has_attachments && !hasCompleteAttachments) {
        fetchMessages();
        return;
      }

      setMessages((prev) => {
        if (prev.some((msg) => msg.id === data.id)) {
          return prev.map((msg) =>
            msg.id === data.id ? { ...msg, ...data } : msg,
          );
        }

        if (!data.is_read && data.sender?.id !== currentUser?.id) {
          setUnreadCount((c) => c + 1);
        }

        return [...prev, data];
      });

      scrollToBottom();
    };

    channel.bind("message.transport.sent", handleNewMessage);

    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
      processedMessageIds.current.clear();
    };
  }, [show, order, currentUser]);

  useEffect(() => {
    if (show && order && unreadCount > 0 && !hasMarkedAsRead) {
      markMessagesAsRead();
    }
  }, [show, order, unreadCount, hasMarkedAsRead]);

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !order) return;

    try {
      setSending(true);
      setError(null);

      const transportId = order.transport?.id || order.transport_id;
      const orderId = order.id;

      if (!transportId || !orderId) {
        setError("Missing transport or order ID");
        return;
      }

      const formData = new FormData();
      formData.append("message", newMessage.trim());
      if (selectedFile) {
        formData.append("attachments[]", selectedFile);
      }

      const response = await userAPI.transportOrderChat.sendMessage(
        transportId,
        orderId,
        formData,
        true,
      );

      if (response.success) {
        setNewMessage("");
        setSelectedFile(null);
        setFilePreview(null);

        const newMsg = response.data?.data || response.data;
        if (newMsg && newMsg.id) {
          setMessages((prev) => {
            if (processedMessageIds.current.has(newMsg.id)) return prev;
            processedMessageIds.current.add(newMsg.id);
            return [...prev, newMsg];
          });
          scrollToBottom();
        }

        if (selectedFile) {
          setTimeout(() => fetchMessages(), 500);
        }
      } else {
        setError(response.message || "Failed to send message");
      }
    } catch (err) {
      console.error("❌ Error sending message:", err);
      setError(err.message || "An error occurred while sending message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }

    setSelectedFile(file);
    setError(null);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (show && order) {
      fetchMessages();
    } else {
      setMessages([]);
      setCurrentUser(null);
      setUnreadCount(0);
      setHasMarkedAsRead(false);
      setIsChatEnabled(true);
      processedMessageIds.current.clear();
    }
  }, [show, order]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (externalUnreadCount !== undefined) {
      setUnreadCount(externalUnreadCount);
    }
  }, [externalUnreadCount]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleModalClose = () => {
    setMessages([]);
    setNewMessage("");
    setError(null);
    setCurrentUser(null);
    setUnreadCount(0);
    setSelectedFile(null);
    setFilePreview(null);
    setHasMarkedAsRead(false);
    setIsChatEnabled(true);
    processedMessageIds.current.clear();

    if (pusherRef.current) {
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    handleClose();
  };

  const isCurrentUser = (message) =>
    currentUser && message.sender?.id === currentUser.id;

  const renderAttachment = (attachment) => {
    const isImage =
      attachment.is_image ||
      (attachment.mime_type && attachment.mime_type.startsWith("image/")) ||
      (attachment.file_url &&
        /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(
          attachment.file_url,
        )) ||
      (attachment.filename &&
        /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(attachment.filename));

    if (isImage && attachment.file_url) {
      return (
        <div className="mb-1">
          <img
            src={attachment.file_url}
            alt={attachment.filename || "image"}
            className="chat-bubble-image"
            onClick={() => window.open(attachment.file_url, "_blank")}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      );
    }

    return (
      <a
        href={attachment.file_url}
        target="_blank"
        rel="noopener noreferrer"
        className="chat-file-attachment"
      >
        <FaFile size={14} />
        <span className="text-truncate" style={{ maxWidth: 160 }}>
          {attachment.filename}
        </span>
        {attachment.file_size_formatted && (
          <small style={{ opacity: 0.7 }}>
            {attachment.file_size_formatted}
          </small>
        )}
      </a>
    );
  };

  if (!order) return null;

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold">
          🚚 {t("clientTable.orderDetails.title")} - {order.order_id}
          {unreadCount > 0 && (
            <Badge bg="danger" className="ms-2">
              {unreadCount}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light">
        {/* Order Info */}
        <Card className="mb-3 shadow-sm border-0 rounded-3">
          <Row className="g-3 align-items-center p-3">
            <Col md={3} className="text-center">
              <h6 className="fw-bold mt-2 mb-0">
                {order.driver_name || t("clientTable.orderDetails.noDriver")}
              </h6>
              <div className="d-flex align-items-center justify-content-center mb-1">
                <FaStar className="text-warning me-1" />
                <span className="text-warning fw-semibold">
                  {order.average_rating ||
                    t("clientTable.orderDetails.noRatings")}
                </span>
              </div>
              <small className="text-muted">
                {t("driverCompleted.driverInfo.reviews")}:{" "}
                {order.total_reviews || 0}
              </small>
              <br />
              <small className="text-muted">
                {t("driverCompleted.driverInfo.vehicle")}: {order.vehicle_type}{" "}
                - {order.vehicle_number}
              </small>
            </Col>
            <Col md={9}>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h3 className="fw-bold text-success">
                  {order.price || "0.00"}
                  <FaEuroSign className="me-1" />
                  {t("netto")} + MwSt
                </h3>
                <Badge
                  bg={getStatusVariant(order.status)}
                  className="text-uppercase"
                >
                  {order.status}
                </Badge>
              </div>

              <p className="mb-1">
                <strong>{t("clientTable.orderDetails.orderPlaced")}:</strong>{" "}
                {formatDate(order.created_at)} {formatTime(order.created_at)}
              </p>

              {order.transport && (
                <>
                  <p className="mb-1">
                    <strong>{t("clientTable.orderDetails.from")}:</strong>{" "}
                    {order.transport.booking_from}
                  </p>
                  <p className="mb-1">
                    <strong>{t("clientTable.orderDetails.to")}:</strong>{" "}
                    {order.transport.booking_to}
                  </p>
                  <p className="mb-1">
                    <strong>{t("clientTable.orderDetails.distance")}:</strong>{" "}
                    {order.transport.distance}
                  </p>
                  {order.info && (
                    <p className="mb-1">
                      <strong>
                        {t("clientTable.orderDetails.additional_info")}:
                      </strong>{" "}
                      {order.info}
                    </p>
                  )}
                </>
              )}

              {order.completed_date && (
                <p className="mb-0 text-muted">
                  <small>
                    {t("clientTable.orderDetails.completed")}:{" "}
                    {formatDate(order.completed_date)}
                  </small>
                </p>
              )}
            </Col>
          </Row>
        </Card>

        {/* Chat */}
        <div
          className="modal-chat-container mb-3"
          style={{
            borderRadius: 16,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <div className="chat-header" style={{ borderRadius: 0 }}>
            <div className="chat-header-info">
              <div className="chat-header-avatar">
                {currentUser?.image ? (
                  <img
                    src={currentUser.image}
                    alt="Driver"
                    className="img-fluid rounded-circle"
                  />
                ) : (
                  currentUser?.name
                    ? currentUser.name.charAt(0).toUpperCase()
                    : "C"
                )}
              </div>
              <div className="chat-header-text">
                <h6>
                  {t("clientTable.orderDetails.transportOrder")} #{order.id}
                </h6>
                <small>{t("clientTable.orderDetails.chatWithDriver")}</small>
              </div>
            </div>
            {unreadCount > 0 && (
              <Badge bg="light" text="dark" pill style={{ fontSize: 11 }}>
                {unreadCount} {t("clientTable.orderDetails.unreadMessages")}
              </Badge>
            )}
          </div>

          <div className="modal-chat-body">
            {error && (
              <Alert
                variant="danger"
                className="py-2 mx-2 mb-2"
                style={{ fontSize: 13 }}
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <div className="chat-empty-state">
                <Spinner animation="border" style={{ color: "#ff6600" }} />
                <p className="mt-2">
                  {t("clientTable.orderDetails.loadingMessages")}
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty-state">
                <div className="empty-icon">💬</div>
                <h6>{t("clientTable.orderDetails.noMessages")}</h6>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isSent = isCurrentUser(msg);
                const offerData = parseOfferCardMessage(msg.message);
                const ratingData = parseRatingCardMessage(msg.message);

                return (
                  <div
                    key={msg.id || idx}
                    className={`chat-message-row ${isSent ? "sent" : "received"}`}
                  >
                    <div
                      className={`chat-bubble ${isSent ? "sent" : "received"}`}
                      style={
                        offerData
                          ? {
                              background: "transparent",
                              boxShadow: "none",
                              padding: "4px 0",
                            }
                          : undefined
                      }
                    >
                      {!isSent &&
                        (idx === 0 || isCurrentUser(messages[idx - 1])) && (
                          <div className="chat-bubble-sender">
                            {msg.sender?.name ||
                              t("clientTable.orderDetails.unknown")}
                          </div>
                        )}

                      {msg.attachments?.length > 0 && (
                        <div>
                          {msg.attachments.map((attachment, attIdx) => (
                            <div key={attIdx}>
                              {renderAttachment(attachment)}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ✅ كارت العرض أو نص عادي */}
                      {/* {offerData ? (
                        <OfferMessageCard
                          data={{
                            ...offerData,
                            status: resolveOfferStatus(offerData),
                          }}
                        />
                      ) : (
                        msg.message && (
                          <div className="chat-bubble-text">{msg.message}</div>
                        )
                      )} */}

                      {offerData ? (
                        <OfferMessageCard data={{ ...offerData, status: resolveOfferStatus(offerData) }} />
                      ) : ratingData ? (
                        <RatingMessageCard data={ratingData} />
                      ) : (
                        msg.message && <div className="chat-bubble-text">{msg.message}</div>
                      )}

                      <div className="chat-bubble-meta">
                        <span>
                          {msg.created_at
                            ? (() => {
                                const d = parseDate(msg.created_at);
                                return d
                                  ? d.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : msg.created_at;
                              })()
                            : ""}
                        </span>
                        {isSent && (
                          <span
                            className={`chat-tick ${msg.is_read ? "read" : "sent-tick"}`}
                          >
                            {msg.is_read ? "✓✓" : "✓"}
                          </span>
                        )}
                        {!isSent && !msg.is_read && (
                          <span style={{ color: "#ff6600", fontWeight: 600 }}>
                            {t("clientTable.orderDetails.unread")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {filePreview && (
            <div
              className="chat-attachment-preview"
              style={{ margin: 0, borderRadius: 0 }}
            >
              <span className="chat-attachment-item">
                <img
                  src={filePreview}
                  alt="preview"
                  style={{
                    width: 28,
                    height: 28,
                    objectFit: "cover",
                    borderRadius: 4,
                  }}
                />
                <span className="text-truncate" style={{ maxWidth: 140 }}>
                  {selectedFile?.name}
                </span>
                <button className="remove-btn" onClick={removeSelectedFile}>
                  <FaTimes size={10} />
                </button>
              </span>
            </div>
          )}

          {/* ✅ الشرط الصحيح: isChatEnabled = true → الشات شغال */}
          {!isChatEnabled ? (
            <div className="chat-disabled-overlay p-3 text-center">
              <div className="chat-disabled-content">
                <FaLock size={25} className="text-muted mb-3" />
                <h6 className="text-muted">
                  {t("clientTable.orderDetails.chat_is_disabled")}
                </h6>
                <p className="text-muted small">
                  {t("clientTable.orderDetails.chat_disabled_description")}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="modal-chat-input">
                <button
                  className="chat-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || uploading}
                  type="button"
                >
                  <FaImage size={18} />
                </button>
                <Form.Control
                  className="chat-text-input"
                  placeholder={t("clientTable.orderDetails.typeMessage")}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending || uploading}
                  as="textarea"
                  rows={1}
                  style={{ resize: "none" }}
                />
                <button
                  className="chat-send-btn"
                  onClick={sendMessage}
                  disabled={
                    sending ||
                    uploading ||
                    (newMessage.trim() === "" && !selectedFile)
                  }
                  type="button"
                >
                  {sending || uploading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaPaperPlane size={16} />
                  )}
                </button>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
              />
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="secondary"
          onClick={handleModalClose}
          className="rounded-pill px-3"
        >
          {t("common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}